import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import type {
  AgeDistribution,
  GenderDistribution,
  TopicCount,
  SentimentBreakdown,
} from '../../types/admin';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

// Color palettes
const COLORS = {
  blue: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
  gender: ['#3b82f6', '#ec4899', '#9ca3af'],
  sentiment: ['#22c55e', '#eab308', '#ef4444'],
  topics: [
    '#3b82f6',
    '#8b5cf6',
    '#ec4899',
    '#f97316',
    '#eab308',
    '#22c55e',
    '#06b6d4',
    '#6366f1',
    '#a855f7',
    '#14b8a6',
  ],
};

interface AgeDistributionChartProps {
  data: AgeDistribution[];
}

export function AgeDistributionChart({ data }: AgeDistributionChartProps) {
  // Filter out zero counts for cleaner display
  const filteredData = data.filter((d) => d.count > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Age Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                dataKey="label"
                type="category"
                width={100}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => [
                  `${value} patients`,
                  'Count',
                ]}
              />
              <Bar dataKey="count" fill={COLORS.blue[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

interface GenderPieChartProps {
  data: GenderDistribution[];
}

export function GenderPieChart({ data }: GenderPieChartProps) {
  // Filter out zero counts and transform data for recharts
  const filteredData = data
    .filter((d) => d.count > 0)
    .map((d) => ({ name: d.gender, value: d.count, percentage: d.percentage }));

  if (filteredData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Gender Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            No gender data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Gender Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={filteredData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              >
                {filteredData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS.gender[index % COLORS.gender.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [`${value} patients`, 'Count']}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

interface TopTopicsChartProps {
  data: TopicCount[];
}

export function TopTopicsChart({ data }: TopTopicsChartProps) {
  // Take top 8 topics
  const topData = data.slice(0, 8);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Top Topics</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                dataKey="topic"
                type="category"
                width={120}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value) => [
                  `${value} conversations`,
                  'Count',
                ]}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {topData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS.topics[index % COLORS.topics.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

interface SentimentChartProps {
  data: SentimentBreakdown[];
}

export function SentimentChart({ data }: SentimentChartProps) {
  // Reorder to Positive, Neutral, Negative
  const orderedData = ['Positive', 'Neutral', 'Negative']
    .map((sentiment) => data.find((d) => d.sentiment === sentiment))
    .filter((d): d is SentimentBreakdown => d !== undefined);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Sentiment Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={orderedData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="sentiment" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value) => [
                  `${value} conversations`,
                  'Count',
                ]}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {orderedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.sentiment === 'Positive'
                        ? COLORS.sentiment[0]
                        : entry.sentiment === 'Neutral'
                          ? COLORS.sentiment[1]
                          : COLORS.sentiment[2]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
